import React, { createContext, useState, useCallback } from 'react';

export interface ToastState {
  message: React.ReactNode;
  isError: boolean;
  visible: boolean;
  /** show() spreads caller args verbatim; tolerate extras without widening reads. */
  [extra: string]: unknown;
}

export interface ToastContextValue {
  show: (args?: Partial<ToastState>) => void;
  hide: () => void;
  toast: ToastState;
}

const initialToast: ToastState = {
  message: '',
  isError: false,
  visible: false,
};

// Default is a cast, not null: every consumer lives under the provider, and the
// ~30 existing `useContext` destructures must keep compiling unchanged (B1).
export const ToastContext = createContext<ToastContextValue>({} as ToastContextValue);

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toast, setToast] = useState<ToastState>(initialToast);

  const show = useCallback((args?: Partial<ToastState>) => {
    setToast({ ...initialToast, visible: true, ...args });
  }, []);

  const hide = useCallback(() => {
    setToast({ ...toast, visible: false });
  }, [toast]);

  return (
    <ToastContext.Provider
      value={{
        hide,
        show,
        toast,
      }}
    >
      {children}
    </ToastContext.Provider>
  );
};
